import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { getSingleTrackerRedux } from "../../../../../redux/slices/trackers/trackers.asyncActions";
import { getSourceTabs, TAB_LABELS } from "../SourceModal.config";
import {
    Source,
    SourceModalMode,
    TabType,
    Tracker,
} from "../SourceModal.types";

interface UseSourceModalOptions {
    source: Source;
    tracker?: Tracker;
    onClose?: () => void;
    onRefresh?: () => void;
    mode?: SourceModalMode;
    initialTab?: TabType | null;
}

interface UseSourceModalReturn {
    activeTab: TabType;
    isDeleteDialogOpen: boolean;
    availableTabs: TabType[];

    setActiveTab: (tab: TabType) => void;
    openDeleteDialog: () => void;
    closeDeleteDialog: () => void;
    refreshTracker: () => void;
    handleClose: () => void;

    getTabLabel: (tab: TabType, language: any) => string;
    hasTab: (tab: TabType) => boolean;
}

export const useSourceModal = ({
    source,
    tracker,
    onClose,
    onRefresh,
    mode,
    initialTab,
}: UseSourceModalOptions): UseSourceModalReturn => {
    const dispatch = useDispatch();
    const params = useParams<{ id: string }>();

    const availableTabs = useMemo(() => {
        return getSourceTabs(source?.source_type?.code, mode);
    }, [source]);

    const defaultTab =
        source?.status === 0 && availableTabs.includes("reconnect")
            ? "reconnect"
            : availableTabs[0] || "overview";

    const [activeTab, setActiveTab] = useState<TabType>(defaultTab);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    useEffect(() => {
        if (initialTab && availableTabs.includes(initialTab)) {
            setActiveTab(initialTab);
        } else if (
            source?.status === 0 &&
            availableTabs.includes("reconnect")
        ) {
            setActiveTab("reconnect");
        } else {
            setActiveTab(availableTabs[0] || "overview");
        }
    }, [initialTab, source?.id, source?.status, availableTabs]);

    const openDeleteDialog = useCallback(() => {
        setIsDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setIsDeleteDialogOpen(false);
    }, []);

    const refreshTracker = useCallback(() => {
        if (onRefresh) {
            onRefresh();
        } else if (params.id) {
            dispatch(getSingleTrackerRedux(params.id) as any);
        }
    }, [dispatch, params.id, onRefresh]);

    const handleClose = useCallback(() => {
        setIsDeleteDialogOpen(false);
        onClose?.();
    }, [onClose]);

    const getTabLabel = useCallback((tab: TabType, language: any): string => {
        const tabConfig = TAB_LABELS[tab];
        if (!tabConfig) return tab;

        const keys = tabConfig.key.split(".");
        let value: any = language;
        for (const key of keys) {
            value = value?.[key];
            if (value === undefined) break;
        }

        return value || tabConfig.default;
    }, []);

    const hasTab = useCallback(
        (tab: TabType): boolean => {
            return availableTabs.includes(tab);
        },
        [availableTabs],
    );

    return {
        activeTab,
        isDeleteDialogOpen,
        availableTabs,

        setActiveTab,
        openDeleteDialog,
        closeDeleteDialog,
        refreshTracker,
        handleClose,

        getTabLabel,
        hasTab,
    };
};

export default useSourceModal;
